/** @format */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { to, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, {
    ForwardRefRenderFunction,
    ReactNode,
    Ref,
    forwardRef,
    useEffect,
    useMemo,
    useState,
} from 'react';
import styled from 'styled-components';

import {
    Badge,
    BadgeColour,
    BadgeSize,
} from '@toucan/client/shared/components/badge';
import { useMounted } from '@toucan/client/shared/hooks/use-mounted';

import { styles } from './basket-button.styles';

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type BasketButtonProps = {
    numItemsInBasket: number;
    buttonRef?: Ref<HTMLElement>;
    children?: ReactNode;
    'data-testid'?: string;
    className?: string;
    onClick?: () => void;
};

const StyledBasketButton = styled('div')`
    ${styles}
`;

const BasketButtonWithRef: ForwardRefRenderFunction<
    HTMLElement,
    BasketButtonProps
> = (
    { numItemsInBasket, children, ...otherProps }: BasketButtonProps,
    ref: Ref<HTMLElement>
) => {
    const isMounted = useMounted();
    const { t } = useTranslation();
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const [playAnimation, setPlayAnimation] = useState<boolean>(false);
    const [previousNumItemsInBasket, setPreviousNumItemsInBasket] =
        useState<number>(numItemsInBasket);

    useEffect(() => {
        if (numItemsInBasket > previousNumItemsInBasket) {
            setPlayAnimation(true);
        }
        setPreviousNumItemsInBasket(numItemsInBasket);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [numItemsInBasket]);

    const text = useMemo(
        () =>
            numItemsInBasket === 0 // eslint-disable-line no-nested-ternary -- Legacy code: do not use as an example.
                ? ''
                : numItemsInBasket > 9
                  ? '9+'
                  : `${numItemsInBasket}`,
        [numItemsInBasket]
    );

    const badgeProps = { size: BadgeSize.REGULAR, text };

    const { time } = useSpring({
        from: { time: 0 },
        time: 1,
        config: { duration: 1000 },
        // stop the animation playing on mount
        immediate: !playAnimation,
        // play the animation whenever play animation is true
        reset: playAnimation,
        onRest: () => {
            // set playing to false after the animation completes
            setPlayAnimation(false);
        },
    });

    // Tada!! animation
    const degrees = 20;
    const animation = {
        transform: to(
            [
                time.to({
                    range: [0, 0.1, 0.2, 0.3, 0.9, 1],
                    output: [1, 0.9, 0.9, 1.1, 1.1, 1],
                }),
                time.to({
                    range: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
                    output: [
                        0,
                        -degrees,
                        -degrees,
                        degrees,
                        -degrees,
                        degrees,
                        -degrees,
                        degrees,
                        -degrees,
                        degrees,
                        0,
                    ],
                }),
            ],
            (scale, rotate) =>
                `scale3d(${scale}, ${scale}, ${scale}) rotate3d(0, 0, 1, ${rotate}deg)`
        ),
    };

    return (
        <StyledBasketButton
            aria-label={t('Basket')}
            buttonRef={ref}
            {...otherProps}
        >
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {text && (
                <div className="badge">
                    <Badge
                        {...badgeProps}
                        colour={BadgeColour.SOL}
                        data-testid="icon-button-badge__badge"
                        animation={isMounted() ? animation : {}}
                    >
                        {badgeProps.text}
                    </Badge>
                </div>
            )}
            <Icon
                variant={IconVariant.ACTIONABLE_SECONDARY}
                size={IconSize.L}
                className="icon-button"
                data-testid="icon-button-badge__button"
            >
                {children}
            </Icon>
        </StyledBasketButton>
    );
};

export const BasketButton = forwardRef(BasketButtonWithRef);
